// El Elo, adaptado a 2v2.
//
// ┌─ La formula ────────────────────────────────────────────────────────┐
// │                                                                     │
// │   Ra = (elo(a1) + elo(a2)) / 2       el equipo vale el promedio     │
// │   Ea = 1 / (1 + 10^((Rb-Ra)/400))    probabilidad de que gane A     │
// │   Sa = 1 si gano A, 0 si no          ganar es ganar                 │
// │                                                                     │
// │   M  = 0.75 + 0.125 x min(dif, 6)    el margen modula               │
// │   K  = 40 los primeros 30 partidos, 28 despues                      │
// │                                                                     │
// │   D  = K x M x (Sa - Ea)                                            │
// │   los dos de A suman D, los dos de B restan D                       │
// └─────────────────────────────────────────────────────────────────────┘
//
// Cuatro decisiones, y por que:
//
// 1. CADA JUGADOR SE LLEVA EL DELTA COMPLETO, no la mitad. Dos suman y dos
//    restan, asi que el sistema sigue siendo de suma cero (el promedio del
//    grupo queda clavado en 1000 para siempre) y la magnitud del movimiento se
//    parece a la de un Elo 1v1, que es lo que la gente espera ver.
//
// 2. EL MARGEN MULTIPLICA, NO CAMBIA S. Si se usara S = games/(games totales),
//    un 6-4 valdria 0,6 y GANAR TE PODRIA BAJAR EL ELO siendo favorito. Un 6-4
//    es una victoria: S vale 1 y el margen solo modula cuanto valio.
//
// 3. K DEPENDE DEL NUMERO DE PARTIDO, NO DEL JUGADOR. Un K distinto por
//    persona (40 para el nuevo, 28 para el veterano) rompe la suma cero. Como
//    todos arrancan juntos, un K alto los primeros 30 partidos calibra rapido
//    a todo el grupo. Al que tiene pocos partidos se le muestra la etiqueta
//    "provisorio": el problema de confianza se resuelve mostrando, no tocando
//    la formula.
//
// 4. NO SE REDONDEA. Se guarda REAL y se redondea al mostrar. Redondear al
//    guardar acumularia distinto segun desde donde arranca el recalculo, y
//    romperia la idempotencia (ver el test de idempotencia).

import type {
  IdJugador,
  MovimientoElo,
  PartidoCalculado,
  PartidoParaElo,
} from './tipos';

export const ELO_INICIAL = 1000;

export const K_CALIBRACION = 40;
export const K_NORMAL = 28;
/** Cuantos partidos de la historia del grupo usan el K alto. */
export const PARTIDOS_CALIBRACION = 30;

/** Debajo de esto, el Elo se muestra como "provisorio". */
export const PARTIDOS_PARA_CONFIAR = 8;

/** Tope del margen: mas de 6 games de diferencia no suma mas castigo. */
const TOPE_MARGEN = 6;

/** Probabilidad de que el equipo con rating `ra` le gane al de rating `rb`. */
export function probabilidadEsperada(ra: number, rb: number): number {
  return 1 / (1 + 10 ** ((rb - ra) / 400));
}

/** El rating de una pareja es el promedio de los dos. */
export function ratingEquipo(elo1: number, elo2: number): number {
  return (elo1 + elo2) / 2;
}

/** Cuanto pesa el margen del set. 6-5 -> 0.875 · 6-4 -> 1.00 · 6-0 -> 1.50 */
export function factorMargen(gamesA: number, gamesB: number): number {
  const diferencia = Math.min(Math.abs(gamesA - gamesB), TOPE_MARGEN);
  return 0.75 + 0.125 * diferencia;
}

/** El K que corresponde al partido numero `numero` de la historia del grupo. */
export function factorK(numero: number): number {
  return numero <= PARTIDOS_CALIBRACION ? K_CALIBRACION : K_NORMAL;
}

/**
 * Pasa un partido por la formula.
 *
 * `numero` es la posicion del partido en la historia del grupo (1 = el primero
 * de todos), no en la del jugador: de eso depende el K.
 */
export function calcularPartido(
  partido: PartidoParaElo,
  eloDe: (jugador: IdJugador) => number,
  numero: number,
): PartidoCalculado {
  const [a1, a2] = partido.equipoA;
  const [b1, b2] = partido.equipoB;

  const eloA1 = eloDe(a1);
  const eloA2 = eloDe(a2);
  const eloB1 = eloDe(b1);
  const eloB2 = eloDe(b2);

  const esperadaA = probabilidadEsperada(
    ratingEquipo(eloA1, eloA2),
    ratingEquipo(eloB1, eloB2),
  );

  const ganoA = partido.games_a > partido.games_b;
  const puntajeA = ganoA ? 1 : 0;

  const delta =
    factorK(numero) * factorMargen(partido.games_a, partido.games_b) * (puntajeA - esperadaA);

  const mover = (jugador: IdJugador, antes: number, signo: 1 | -1): MovimientoElo => ({
    jugador,
    antes,
    despues: antes + signo * delta,
  });

  return {
    partido,
    numero,
    esperadaA,
    delta,
    ganoA,
    movimientos: [
      mover(a1, eloA1, 1),
      mover(a2, eloA2, 1),
      mover(b1, eloB1, -1),
      mover(b2, eloB2, -1),
    ],
  };
}

/**
 * El orden canonico de la historia. Todo recalculo lo recorre asi, siempre.
 *
 * El desempate por `id` no es decorativo: si dos personas cargan un partido el
 * mismo dia con el mismo `orden`, sin el la historia podria recorrerse en
 * distinto orden en dos corridas y el recalculo dejaria de ser idempotente.
 */
export function ordenCanonico(a: PartidoParaElo, b: PartidoParaElo): number {
  if (a.jugado_el !== b.jugado_el) return a.jugado_el < b.jugado_el ? -1 : 1;
  if (a.orden !== b.orden) return a.orden - b.orden;
  return a.id - b.id;
}

export interface EstadoElo {
  /** Elo de cada jugador. Los que no estan arrancan en ELO_INICIAL. */
  elos: Map<IdJugador, number>;
  /** Cuantos partidos confirmados hay ANTES del primero de esta corrida. */
  partidosPrevios: number;
}

/**
 * Recorre una tanda de partidos aplicando la formula, en orden canonico.
 *
 * Es una funcion PURA: no toca el `estado` que recibe, devuelve uno nuevo.
 * Esa es la razon por la que recalcular desde el principio y desde el medio
 * dan exactamente lo mismo.
 */
export function recorrerHistoria(
  partidos: readonly PartidoParaElo[],
  estado: EstadoElo,
): { calculados: PartidoCalculado[]; elos: Map<IdJugador, number> } {
  const elos = new Map(estado.elos);
  const eloDe = (jugador: IdJugador) => elos.get(jugador) ?? ELO_INICIAL;

  const enOrden = [...partidos].sort(ordenCanonico);
  const calculados: PartidoCalculado[] = [];

  let numero = estado.partidosPrevios;
  for (const partido of enOrden) {
    numero += 1;
    const calculado = calcularPartido(partido, eloDe, numero);
    for (const movimiento of calculado.movimientos) {
      elos.set(movimiento.jugador, movimiento.despues);
    }
    calculados.push(calculado);
  }

  return { calculados, elos };
}

/** Para mostrar. El Elo se guarda con decimales y se redondea recien aca. */
export function eloVisible(elo: number): number {
  return Math.round(elo);
}
